// Route Code Edited 7/9/2026
/**
 * route-checkout-subtotal-update.js
 *
 * Updates Route's cloned checkout button to display the Route package
 * protection quote instead of the cart subtotal, formatted to match the
 * store's existing currency/locale style (decimal separator, thousands
 * separator, and currency symbol), with the currency code omitted.
 *
 * Supports multiple simultaneous instances of the cloned button on a
 * single page (e.g. a cart drawer and a cart page rendering at the same
 * time), and works across Route's Standard and Paired widget layouts.
 *
 * Dependencies:
 *   - Requires Route to be installed (detected via #route-widget-observer-root).
 *   - Requires window.Shopify.formatMoney and window.theme.moneyFormat for
 *     fallback currency formatting, with Intl.NumberFormat as a final fallback.
 * 
 * Installation:
 *   Add this script tag at the bottom of layout/theme.liquid (before </body>):
 *     <script src="{{ 'route-checkout-subtotal-update.js' | asset_url }}" defer></script>
 */
(function () {
  const CLONED_BUTTON_SELECTOR   = '[data-route-cloned-button="true"]';
  const ROUTE_PRICE_SELECTOR     = '#pw-coverage-description';
  const ROUTE_ROOT_ID            = 'route-widget-observer-root';
  const PRICE_CORRECTED_ATTR     = 'data-route-price-corrected';
  const SEPARATOR_CHAR           = '+';
  const ORIGINAL_BUTTON_SELECTOR = 'button[type="submit"][name="checkout"]';
  const PRICE_FOUND_EVENT        = 'route-price-element-found';

  const instanceState = new WeakMap();
  // { originalBtn, guardObserver, subtotalObserver,
  //   lastKnownGood, lastKnownGoodWithCurrency, intercepted }

  let outerObserver = null;
  let routePriceObserver = null;
  let pendingScan = false;
  let priceElementSeen = false;

  // ─── Locale-agnostic numeric token handling ─────────────────────────────────

  function extractNumericToken(text) {
    const match = text.match(/\d[\d.,]*\d|\d/);
    return match ? match[0] : null;
  }

  function parseNumericToken(token) {
    if (!token) return null;
    const sepMatches = [...token.matchAll(/[.,]/g)];
    if (!sepMatches.length) {
      const value = parseInt(token, 10);
      if (isNaN(value)) return null;
      return { value, decimalDigits: 0, decimalSep: null, groupSep: null };
    }
    const lastSep = sepMatches[sepMatches.length - 1];
    const decimalSep = token[lastSep.index];
    const groupSep = decimalSep === '.' ? ',' : '.';
    const decimalDigits = token.length - lastSep.index - 1;
    const normalized = token.split(groupSep).join('').replace(decimalSep, '.');
    const value = parseFloat(normalized);
    if (isNaN(value)) return null;
    return { value, decimalDigits, decimalSep, groupSep };
  }

  function formatValueLikeToken(value, formatInfo) {
    const { decimalDigits, decimalSep, groupSep } = formatInfo;
    const fixed = value.toFixed(decimalDigits);
    const [whole, dec] = fixed.split('.');
    const effectiveGroupSep = groupSep || ',';
    const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, effectiveGroupSep);
    return decimalDigits > 0 && decimalSep ? groupedWhole + decimalSep + dec : groupedWhole;
  }

  // ─── Formatting ────────────────────────────────────────────────────────────

  function stripTrailingCurrencyCode(str) {
    return str.replace(/\s*[A-Z]{2,4}\s*$/, '');
  }

  function formatLikeSubtotal(routeValue, originalBtn) {
    const subtotalSpan = originalBtn ? originalBtn.querySelector('span') : null;

    if (subtotalSpan) {
      const subtotalText = subtotalSpan.textContent;
      const subtotalToken = extractNumericToken(subtotalText);
      const subtotalInfo = parseNumericToken(subtotalToken);

      if (subtotalInfo) {
        const formattedAmount = formatValueLikeToken(routeValue, subtotalInfo);
        const withCurrency = subtotalToken
          ? subtotalText.replace(subtotalToken, formattedAmount)
          : subtotalText;
        const display = stripTrailingCurrencyCode(withCurrency);
        return { display, withCurrency };
      }
    }

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      const withCurrency = window.Shopify.formatMoney(Math.round(routeValue * 100), window.theme.moneyFormat);
      const display = stripTrailingCurrencyCode(withCurrency);
      return { display, withCurrency };
    }
    const fallback = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(routeValue);
    return { display: fallback, withCurrency: fallback };
  }

  function getRoutePriceValue() {
    const el = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!el) return null;
    const token = extractNumericToken(el.textContent);
    const parsed = parseNumericToken(token);
    return parsed ? parsed.value : null;
  }

  function getButtonPrefix(button) {
    let prefix = '';
    button.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) prefix += node.textContent;
    });
    return prefix.trim();
  }

  // ─── Price segment construction ─────────────────────────────────────────────

  function buildPriceSegment(withCurrencyStr, formattedAmountStr) {
    if (!withCurrencyStr) return formattedAmountStr;
    const token = extractNumericToken(withCurrencyStr);
    if (!token) return formattedAmountStr;
    const tokenIndex = withCurrencyStr.indexOf(token);
    let start = tokenIndex;
    while (start > 0 && !/\s/.test(withCurrencyStr[start - 1])) start--;
    const symbolPrefix = withCurrencyStr.slice(start, tokenIndex);
    return symbolPrefix + formattedAmountStr;
  }

  // ─── Write functions ─────────────────────────────────────────────────────────

  function writePriceToSpan(spanEl, formattedAmountStr, withCurrency) {
    const segment = buildPriceSegment(withCurrency, formattedAmountStr);

    for (let i = spanEl.childNodes.length - 1; i >= 0; i--) {
      const node = spanEl.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && extractNumericToken(node.textContent)) {
        node.textContent = segment;
        return;
      }
    }
    const newTextNode = document.createTextNode(segment);
    spanEl.appendChild(newTextNode);
  }

  function writeSeparatorToSpan(spanEl) {
    for (let i = 0; i < spanEl.childNodes.length; i++) {
      const node = spanEl.childNodes[i];
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.textContent.trim() !== SEPARATOR_CHAR) {
          node.textContent = SEPARATOR_CHAR;
        }
        return;
      }
    }
    const b = document.createElement('b');
    b.textContent = SEPARATOR_CHAR;
    spanEl.appendChild(b);
  }

  // ─── Pairing ─────────────────────────────────────────────────────────────────

  function findPairedOriginal(clonedBtn) {
    let node = clonedBtn.parentElement;
    while (node) {
      const matches = node.querySelectorAll(ORIGINAL_BUTTON_SELECTOR);
      if (matches.length >= 1) return matches[0];
      node = node.parentElement;
    }
    return null;
  }

  // ─── Guard (per instance) ───────────────────────────────────────────────────

  function attachGuard(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;
    if (state.guardObserver) state.guardObserver.disconnect();

    state.guardObserver = new MutationObserver(() => {
      if (state.lastKnownGood === null) return;
      const writeTarget = clonedBtn.querySelector('span');
      if (!writeTarget) return;
      writeSeparatorToSpan(writeTarget);
      if (writeTarget.textContent.trim() !== state.lastKnownGood.trim()) {
        const token = extractNumericToken(state.lastKnownGood);
        if (token) {
          writePriceToSpan(writeTarget, token, state.lastKnownGoodWithCurrency);
        }
      }
    });

    state.guardObserver.observe(clonedBtn, { subtree: true, childList: true });
  }

  // ─── Reconcile (per instance) ───────────────────────────────────────────────

  function reconcile(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;
    const writeTarget = clonedBtn.querySelector('span');
    if (!writeTarget) return;

    const routeValue = getRoutePriceValue();
    if (routeValue === null) return;

    const { display, withCurrency } = formatLikeSubtotal(routeValue, state.originalBtn);
    const separatorNeedsWrite = (() => {
      for (let i = 0; i < writeTarget.childNodes.length; i++) {
        const node = writeTarget.childNodes[i];
        if (node.nodeType === Node.ELEMENT_NODE) return node.textContent.trim() !== SEPARATOR_CHAR;
      }
      return true;
    })();

    if (display === state.lastKnownGood && !separatorNeedsWrite) return;

    if (state.guardObserver) state.guardObserver.disconnect();
    writeSeparatorToSpan(writeTarget);
    const token = extractNumericToken(display);
    if (!token) return;
    writePriceToSpan(writeTarget, token, withCurrency);
    state.lastKnownGood = display;
    state.lastKnownGoodWithCurrency = withCurrency;
    clonedBtn.setAttribute(PRICE_CORRECTED_ATTR, '');
    attachGuard(clonedBtn);

    const prefix = getButtonPrefix(clonedBtn);
    clonedBtn.setAttribute('aria-label', prefix ? `${prefix} ${withCurrency}` : withCurrency);
  }

  function reconcileAll() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (instanceState.has(btn)) reconcile(btn);
    });
  }

  // ─── innerHTML interception (per instance) ─────────────────────────────────

  function interceptInnerHTML(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state || state.intercepted) return;
    state.intercepted = true;

    const nativeDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(clonedBtn, 'innerHTML', {
      set(value) {
        nativeDescriptor.set.call(this, value);
        reconcile(clonedBtn);
      },
      get() {
        return nativeDescriptor.get.call(this);
      },
      configurable: true,
      enumerable: true,
    });
  }

  // ─── Per-instance subtotal watcher ──────────────────────────────────────────

  function watchSubtotal(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state || !state.originalBtn) return;
    const subtotalSpan = state.originalBtn.querySelector('span');
    if (!subtotalSpan) return;
    if (state.subtotalObserver) state.subtotalObserver.disconnect();

    state.subtotalObserver = new MutationObserver(() => reconcile(clonedBtn));
    state.subtotalObserver.observe(subtotalSpan, { characterData: true, childList: true, subtree: true });
  }

  // ─── Shared Route price watcher ─────────────────────────────────────────────

  function watchRoutePrice() {
    const routePriceEl = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!routePriceEl) return;
    if (routePriceObserver) routePriceObserver.disconnect();

    routePriceObserver = new MutationObserver(() => reconcileAll());
    routePriceObserver.observe(routePriceEl, { characterData: true, childList: true, subtree: true });
  }

  // ─── Price element arrival detection ────────────────────────────────────────

  function checkMutationsForPriceElement(mutationList) {
    if (priceElementSeen) return;
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode.nodeType !== Node.ELEMENT_NODE) continue;
        if (addedNode.matches && addedNode.matches(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
        if (addedNode.querySelector && addedNode.querySelector(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
      }
    }
  }

  document.addEventListener(PRICE_FOUND_EVENT, () => {
    watchRoutePrice();
    reconcileAll();
  });

  // ─── Cloned button setup ────────────────────────────────────────────────────

  function processClonedButton(btn) {
    if (instanceState.has(btn)) return;

    instanceState.set(btn, {
      originalBtn: findPairedOriginal(btn),
      guardObserver: null,
      subtotalObserver: null,
      lastKnownGood: null,
      lastKnownGoodWithCurrency: null,
      intercepted: false,
    });

    interceptInnerHTML(btn);
    attachGuard(btn);
    watchSubtotal(btn);
    reconcile(btn);
  }

  function scanForClonedButtons() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (!instanceState.has(btn)) {
        processClonedButton(btn);
      } else {
        watchSubtotal(btn);
        reconcile(btn);
      }
    });
    watchRoutePrice();
  }

  // ─── Outer observer ──────────────────────────────────────────────────────────

  function attachObserver() {
    if (outerObserver) outerObserver.disconnect();
    if (document.querySelector(ROUTE_PRICE_SELECTOR)) priceElementSeen = true;

    outerObserver = new MutationObserver((mutationList) => {
      checkMutationsForPriceElement(mutationList);

      if (pendingScan) return;
      pendingScan = true;
      requestAnimationFrame(() => {
        pendingScan = false;
        scanForClonedButtons();
      });
    });

    outerObserver.observe(document.body, { childList: true, subtree: true });
    scanForClonedButtons();
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  function waitForRoot() {
    if (document.getElementById(ROUTE_ROOT_ID)) {
      attachObserver();
      return;
    }

    let initTimeout = null;
    const bodyObserver = new MutationObserver(() => {
      if (!document.getElementById(ROUTE_ROOT_ID)) return;
      bodyObserver.disconnect();
      clearTimeout(initTimeout);
      attachObserver();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
    initTimeout = setTimeout(() => bodyObserver.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRoot);
  } else {
    waitForRoot();
  }

  // ─── bfcache ────────────────────────────────────────────────────────────────

  window.addEventListener('pagehide', () => {
    if (outerObserver) outerObserver.disconnect();
    if (routePriceObserver) routePriceObserver.disconnect();
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      const state = instanceState.get(btn);
      if (state) {
        if (state.guardObserver) state.guardObserver.disconnect();
        if (state.subtotalObserver) state.subtotalObserver.disconnect();
      }
    });
  });

  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    priceElementSeen = false;
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      instanceState.delete(btn);
    });
    attachObserver();
  });
})();